import {
  Check,
  CheckCircle,
  PencilSimple,
  Trash,
} from '@phosphor-icons/react'
import {
  Bold,
  Ellipsis,
  Italic,
  Link,
  Reply,
  SmilePlus,
  Strikethrough,
  Underline,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { CommentEditor } from './CommentEditor'
import type { FloatingFormatToolbarItem } from '@/components/editor/FloatingToolbar'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { useCommentMutations } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { useMember } from '@/lib/hooks/useWorkspaceUsers'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { memberAvatarSrc } from '@/lib/utils/memberAvatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  ReactionPickerPopover,
  ReactionPillList,
  useEntityReactions,
} from '@/components/features/reactions'
import { getFrequentEmoji } from '@/components/features/reactions/frequentEmoji'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/useMobile'
import { TiptapRichTextEditor } from '@/components/editor/TiptapRichTextEditor'
import { useMentionUsers } from '@/components/editor/MentionExtension'
import { extractPlainTextFromTiptap } from '@/routes/features/tasks/utils/editorUtils'

// Tiptap handles display styling via prose classes set on the editor element

const COMMENT_EDIT_TOOLBAR_ITEMS: Array<FloatingFormatToolbarItem> = [
  { format: 'bold', label: 'Bold', Icon: Bold },
  { format: 'italic', label: 'Italic', Icon: Italic },
  { format: 'underline', label: 'Underline', Icon: Underline },
  { format: 'strikethrough', label: 'Strike', Icon: Strikethrough },
]

interface CommentEditFormProps {
  comment: any
  onSave: (content: string, plainText: string) => Promise<void>
  onCancel: () => void
}

function CommentEditForm({ comment, onSave, onCancel }: CommentEditFormProps) {
  const { t } = useTranslation('common')
  useMentionUsers()
  const contentRef = useRef<string>(comment.content || '')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleContentChange = useCallback((content: string) => {
    contentRef.current = content
    setError(null)
  }, [])

  const handleSave = async () => {
    const content = contentRef.current
    const plainText = extractPlainTextFromTiptap(content)

    if (!plainText.trim()) {
      setError('Comment cannot be empty')
      return
    }

    if (plainText.length > 2000) {
      setError('Comment must be 2000 characters or less')
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      await onSave(content, plainText)
    } catch (err) {
      setError('Failed to save comment')
      setIsSaving(false)
    }
  }

  return (
    <div className="space-y-2">
      <div className="rounded-md border border-input bg-background">
        <TiptapRichTextEditor
          namespace={`comment-edit-${comment.id}`}
          initialContent={comment.content}
          editable={!isSaving}
          placeholder={t('comments.editPlaceholder')}
          className="px-3"
          contentEditableClassName="min-h-[60px] py-2"
          placeholderClassName="top-2 left-3"
          onContentChange={handleContentChange}
          floatingToolbar={true}
          toolbarItems={COMMENT_EDIT_TOOLBAR_ITEMS}
          enableSlashCommands={false}
          enableMentions={true}
          enableEntityLinks={true}
          ariaLabel="Edit comment"
        />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex gap-2 justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button data-testid="comment-save" size="sm" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  )
}

function CommentContent({ comment }: { comment: any }) {
  if (comment.contentFormat === 'tiptap') {
    return (
      <TiptapRichTextEditor
        namespace={`comment-view-${comment.id}`}
        initialContent={comment.content}
        editable={false}
        floatingToolbar={false}
        enableSlashCommands={false}
        enableMentions={true}
        enableEntityLinks={true}
        className="gap-0"
        contentEditableClassName="min-h-0 py-0 [&>*:last-child]:!mb-0"
      />
    )
  }

  return (
    <div className="text-sm text-foreground leading-relaxed">
      {comment.contentPlainText || comment.content}
    </div>
  )
}

interface CommentItemProps {
  comment: any
  replies?: Array<any>
  entityType?: string
  entityId?: string
  workspaceId?: string
  anchorId?: string | null
  level?: number
  /**
   * `card`: a bordered card per thread (doc comment sidebar, notifications).
   * `stream`: unboxed rows in the task discussion stream (design 28a / 35a).
   */
  variant?: 'card' | 'stream'
  /** Extra classes for the top-level card (e.g. the notifications pane highlight). */
  cardClassName?: string
  /** Marks the card as the one that triggered a notification. */
  highlighted?: boolean
}

export function CommentItem({
  comment,
  replies = [],
  entityType,
  entityId,
  workspaceId,
  anchorId,
  level = 0,
  variant = 'card',
  cardClassName,
  highlighted = false,
}: CommentItemProps) {
  const { t } = useTranslation('common')
  const currentUserId = useLoginStore((state) => state.userId)
  const [isReplying, setIsReplying] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(
    comment.isResolved && level === 0,
  )

  const { updateComment } = useCommentMutations()

  // Auto-collapse when resolved
  useEffect(() => {
    if (comment.isResolved && level === 0) {
      setIsCollapsed(true)
    }
  }, [comment.isResolved, level])

  const handleToggleResolved = async () => {
    try {
      if (!currentUserId) {
        return
      }

      const newResolvedState = !comment.isResolved
      await updateComment(comment.id, {
        isResolved: newResolvedState,
        resolvedAt: newResolvedState ? Date.now() : null,
        resolvedBy: newResolvedState ? currentUserId : null,
      })
    } catch (error) {
      // Error handling
    }
  }

  const isResolved = comment.isResolved

  // Look up who resolved the comment from members table
  const { member: resolvedByMember } = useMember(comment.resolvedBy)
  const resolvedByIsCurrentUser = comment.resolvedBy === currentUserId
  const resolvedByDisplayName = resolvedByIsCurrentUser
    ? 'You'
    : resolvedByMember?.name || resolvedByMember?.email || 'Unknown User'

  const totalComments = 1 + replies.length

  // Collapsed view for resolved threads
  if (isResolved && isCollapsed && level === 0) {
    return (
      <button
        type="button"
        onClick={() => setIsCollapsed(false)}
        className="group flex w-full items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-left transition-colors hover:bg-muted/50"
      >
        <Check className="h-4 w-4 flex-shrink-0 text-green-600 dark:text-green-400" weight="bold" />
        <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{resolvedByDisplayName}</span>
          <span>
            {' · '}
            {totalComments} {totalComments === 1 ? 'comment' : 'comments'}
          </span>
        </span>
        <span className="flex-shrink-0 text-xs text-muted-foreground group-hover:text-foreground">
          Expand
        </span>
      </button>
    )
  }

  // Nested reply: a plain row, no card
  if (level > 0) {
    return <CommentRow comment={comment} />
  }

  const isStream = variant === 'stream'

  return (
    <div className="space-y-2">
      <div
        data-testid="comment-item"
        data-highlighted={highlighted || undefined}
        className={cn(
          isStream
            ? 'space-y-1'
            : 'space-y-2 rounded-lg border border-border bg-card p-3 hover:border-border/80',
          cardClassName,
        )}
      >
        <CommentRow
          comment={comment}
          onReply={entityType && entityId && workspaceId ? () => setIsReplying(!isReplying) : undefined}
          onToggleResolved={handleToggleResolved}
        />

        {/* Replies: in a card, under an edge-to-edge rule; in the stream, indented under the avatar */}
        {replies.length > 0 && (
          <div className={isStream ? 'pl-9' : '-mx-3 border-t border-border px-3 pt-1'}>
            {replies.map((reply) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                entityType={entityType}
                entityId={entityId}
                workspaceId={workspaceId}
                anchorId={anchorId}
                level={level + 1}
              />
            ))}
          </div>
        )}

        {/* Resolved banner: edge-to-edge at the foot of a card, a tinted strip in the stream */}
        {isResolved && (
          <div
            className={cn(
              'border-green-200 bg-green-50 px-3 py-2 dark:border-green-800 dark:bg-green-950',
              isStream ? 'ml-9 rounded-md border' : '-mx-3 -mb-3 rounded-b-lg border-t',
            )}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600 dark:text-green-400" weight="bold" />
                <span className="text-sm text-green-700 dark:text-green-300">
                  <span className="font-medium">{resolvedByDisplayName}</span>{' '}
                  resolved the thread
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs text-green-700 dark:text-green-300 hover:text-green-800 dark:hover:text-green-200"
                onClick={() => setIsCollapsed(true)}
              >
                Collapse ×
              </Button>
            </div>
          </div>
        )}

        {/* Reply editor */}
        {isReplying && entityType && entityId && (
          <div className={isStream ? 'pl-9 pt-1' : 'pt-2'}>
            <CommentEditor
              entityType={entityType}
              entityId={entityId}
              parentCommentId={comment.id}
              anchorId={anchorId ?? comment.anchorId ?? null}
              onSuccess={() => setIsReplying(false)}
              placeholder={t('comments.replyPlaceholder')}
            />
          </div>
        )}
      </div>

      {/* Collapse button outside card */}
      {isResolved && (
        <button
          type="button"
          onClick={() => setIsCollapsed(true)}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 justify-center w-full py-1"
        >
          <span>×</span>
          <span>{t('comments.collapseResolved')}</span>
        </button>
      )}
    </div>
  )
}

interface CommentRowProps {
  comment: any
  /** Top-level only: the hover bar's Reply. */
  onReply?: () => void
  /** Top-level only: Resolve / Reopen in the ⋯ menu. */
  onToggleResolved?: () => void
}

/**
 * One comment: avatar, name · time, body, reaction pills, and the hover bar
 * (3 quick reactions · ☺ · reply · link · ⋯) floating over its top-right
 * corner (design 35a).
 */
function CommentRow({ comment, onReply, onToggleResolved }: CommentRowProps) {
  const { t } = useTranslation('common')
  const currentUserId = useLoginStore((state) => state.userId)
  const { smartTime, dateTime: formatDateTime } = useDateTimeFormatters()
  const isOwner = comment.createdBy === currentUserId
  const [isEditing, setIsEditing] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const rowRef = useRef<HTMLDivElement>(null)

  const { deleteComment: deleteCommentMutation, updateComment } =
    useCommentMutations()
  const { groups, toggle } = useEntityReactions('comment', comment.id)
  const quickReactions = useMemo(() => getFrequentEmoji(3), [])

  // Client-side join with members to get display name
  // Always look up from members table (even for own comments) for consistency
  const { member } = useMember(comment.createdBy)

  // Display name: "You" for own comments, otherwise the member's name
  const displayName = isOwner
    ? 'You'
    : member?.name || member?.email || 'Unknown User'

  // Avatar initials should always use actual member name, not "You"
  const avatarInitials = member?.name
    ? member.name.substring(0, 2).toUpperCase()
    : member?.email
    ? member.email.substring(0, 2).toUpperCase()
    : displayName === 'You'
    ? 'ME'
    : displayName.substring(0, 2).toUpperCase()

  // A copied comment link lands here: bring the row into view.
  useEffect(() => {
    if (window.location.hash === `#comment-${comment.id}`) {
      rowRef.current?.scrollIntoView({ block: 'center' })
    }
  }, [comment.id])

  const handleDelete = async () => {
    if (confirm('Are you sure you want to delete this comment?')) {
      try {
        await deleteCommentMutation(comment.id)
      } catch (error) {
        // Error handling
      }
    }
  }

  const handleEditSave = async (content: string, plainText: string) => {
    await updateComment(comment.id, {
      content,
      contentFormat: 'tiptap',
      contentPlainText: plainText,
    })
    setIsEditing(false)
  }

  const handleCopyLink = async () => {
    const { origin, pathname, search } = window.location
    try {
      await navigator.clipboard.writeText(`${origin}${pathname}${search}#comment-${comment.id}`)
      toast.success(t('comments.linkCopied'))
    } catch {
      // Clipboard blocked: nothing to copy to.
    }
  }

  const createdDate = comment.createdOn
    ? new Date(comment.createdOn)
    : new Date()
  // Comment is edited if updatedOn is more than 1 second after createdOn
  const isEdited =
    comment.updatedOn && comment.createdOn
      ? comment.updatedOn - comment.createdOn > 1000
      : false

  const hasMenu = Boolean(onToggleResolved) || isOwner
  // Phones (design 36b): no hover, and long-press selects text, so ☺ and ⋯
  // are always there; Reply and Copy link move into ⋯.
  const isPhone = useIsMobile()
  const barPinned = pickerOpen || menuOpen
  const barButton =
    'grid size-[26px] place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground'

  return (
    <div
      ref={rowRef}
      id={`comment-${comment.id}`}
      className={cn(
        'group/comment relative -mx-2.5 flex gap-2.5 rounded-[10px] px-2.5 py-2 transition-colors hover:bg-chrome',
        barPinned && 'bg-chrome',
      )}
    >
      <Avatar className="size-[26px] flex-shrink-0">
        <AvatarImage src={memberAvatarSrc(member)} alt={displayName} />
        <AvatarFallback className="text-[11px] font-semibold">
          {avatarInitials}
        </AvatarFallback>
      </Avatar>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-1.5 text-[12.5px]">
          <span className="font-semibold">{displayName}</span>
          {member?.isSuperAgent ? (
            <span
              className="inline-flex items-center rounded bg-primary/10 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-primary"
              title="Posted by the Super Agent (AI)"
            >
              AI
            </span>
          ) : null}
          <time
            className="text-muted-foreground"
            title={formatDateTime(createdDate, { dateStyle: 'medium', timeStyle: 'short' })}
            dateTime={createdDate.toISOString()}
          >
            {smartTime(createdDate)}
          </time>
          {isEdited && (
            <span className="text-muted-foreground italic">
              (edited)
            </span>
          )}
        </div>

        {isEditing ? (
          <CommentEditForm
            comment={comment}
            onSave={handleEditSave}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <CommentContent comment={comment} />
        )}

        <ReactionPillList groups={groups} onToggle={toggle} className="mt-1" />
      </div>

      {isPhone && (
        <div className="absolute right-0 top-0 z-10 flex items-center">
          <ReactionPickerPopover onSelect={toggle} open={pickerOpen} onOpenChange={setPickerOpen} align="end">
            <button
              type="button"
              aria-label={t('reactions.addReaction')}
              className="grid size-9 place-items-center rounded-md text-muted-foreground"
            >
              <SmilePlus className="size-4" />
            </button>
          </ReactionPickerPopover>
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="comment-options"
                aria-label={t('comments.more')}
                className="grid size-9 place-items-center rounded-md text-muted-foreground"
              >
                <Ellipsis className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-44 [&>[role=menuitem]]:min-h-11">
              {onReply && (
                <DropdownMenuItem data-testid="comment-reply" onClick={onReply}>
                  <Reply className="mr-2 h-4 w-4" />
                  {t('comments.reply')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => void handleCopyLink()}>
                <Link className="mr-2 h-4 w-4" />
                {t('comments.copyLink')}
              </DropdownMenuItem>
              {onToggleResolved && (
                <DropdownMenuItem data-testid="comment-resolve" onClick={onToggleResolved}>
                  <CheckCircle className="mr-2 h-4 w-4" weight={comment.isResolved ? 'fill' : 'regular'} />
                  {comment.isResolved ? t('comments.reopen') : t('comments.resolve')}
                </DropdownMenuItem>
              )}
              {isOwner && (
                <DropdownMenuItem data-testid="comment-edit" onClick={() => setIsEditing(true)}>
                  <PencilSimple className="mr-2 h-4 w-4" />
                  {t('comments.edit')}
                </DropdownMenuItem>
              )}
              {isOwner && (
                <DropdownMenuItem data-testid="comment-delete" onClick={handleDelete} className="text-destructive">
                  <Trash className="mr-2 h-4 w-4" />
                  {t('comments.delete')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Hover bar */}
      {!isPhone && (
      <div
        role="toolbar"
        aria-label={t('comments.actions')}
        className={cn(
          'absolute -top-1.5 right-0 z-10 flex items-center gap-px rounded-lg bg-popover p-0.5 shadow-[0_4px_12px_-4px_rgba(31,29,26,.2),0_0_0_1px_var(--border)] transition-opacity',
          barPinned
            ? 'opacity-100'
            : 'pointer-events-none opacity-0 group-has-[:focus-visible]/comment:pointer-events-auto group-has-[:focus-visible]/comment:opacity-100 group-hover/comment:pointer-events-auto group-hover/comment:opacity-100 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100',
        )}
      >
        {quickReactions.map((emoji) => (
          <button
            key={emoji}
            type="button"
            aria-label={t('reactions.reactWith', { emoji })}
            onClick={() => toggle(emoji)}
            className="grid size-[26px] place-items-center rounded-md text-sm leading-none hover:bg-accent"
          >
            {emoji}
          </button>
        ))}
        <span className="mx-0.5 h-4 w-px bg-border" />
        <ReactionPickerPopover onSelect={toggle} open={pickerOpen} onOpenChange={setPickerOpen} align="end">
          <button type="button" aria-label={t('reactions.addReaction')} className={barButton}>
            <SmilePlus className="size-3.5" />
          </button>
        </ReactionPickerPopover>
        {onReply && (
          <button
            type="button"
            data-testid="comment-reply"
            aria-label={t('comments.reply')}
            onClick={onReply}
            className={barButton}
          >
            <Reply className="size-3.5" />
          </button>
        )}
        <button type="button" aria-label={t('comments.copyLink')} onClick={handleCopyLink} className={barButton}>
          <Link className="size-3.5" />
        </button>
        {hasMenu && (
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button type="button" data-testid="comment-options" aria-label={t('comments.more')} className={barButton}>
                <Ellipsis className="size-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onToggleResolved && (
                <DropdownMenuItem data-testid="comment-resolve" onClick={onToggleResolved}>
                  <CheckCircle className="mr-2 h-4 w-4" weight={comment.isResolved ? 'fill' : 'regular'} />
                  {comment.isResolved ? t('comments.reopen') : t('comments.resolve')}
                </DropdownMenuItem>
              )}
              {isOwner && (
                <DropdownMenuItem data-testid="comment-edit" onClick={() => setIsEditing(true)}>
                  <PencilSimple className="mr-2 h-4 w-4" />
                  {t('comments.edit')}
                </DropdownMenuItem>
              )}
              {isOwner && (
                <DropdownMenuItem
                  data-testid="comment-delete"
                  onClick={handleDelete}
                  className="text-destructive"
                >
                  <Trash className="mr-2 h-4 w-4" />
                  {t('comments.delete')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      )}
    </div>
  )
}
